import { makeAutoObservable, runInAction } from 'mobx';
import { IRootStore, IBooksStore, IReadingContext } from '@shared/types';
import { IBookLevel, IBook, ITextEntity, IParashaJump } from '@shared/types';
import { BOOK_LIST } from '../../constants/books.constants';
import { formatResponseToTextEntities } from '../../utils/text-entities-formater.utils';
import { numberToGematria } from '../../utils/text-hebrew.utils';
import { isSiddurReaderEnabled } from '../../utils/feature-switches';

/**
 * 📚 BOOKS CONTROLLER - Controller לניהול ספרים קדושים
 * 
 * **תפקיד ראשי:** Controller שמנהל את מצב הספרים, הטעינה והניווט בטקסטים הקדושים
 * 
 * **Pattern:** Repository + Controller - מנהל נתונים + API calls + business logic
 */
class BooksController implements IBooksStore {
    rootStore: IRootStore;
    isLoading = false;
    // Where the Siddur reader is switched off (prod), the Siddur is not offered
    // at all: the old level-selector Siddur is not fit to put in front of schools.
    books: IBook[] = isSiddurReaderEnabled()
        ? BOOK_LIST
        : BOOK_LIST.filter(book => book.name_en !== 'Siddur_Ashkenaz');

    // Hierarchical levels data
    levelOne: IBookLevel[] = [];
    levelTwo: IBookLevel[] = [];
    levelThree: IBookLevel[] = [];
    levelFour: IBookLevel[] = [];
    levelFive: IBookLevel[] = []; // הוספת רמה חמישית לסידור

    // Current selections at each level
    selectedBook: IBook | null = null;
    selectedLevelOne: IBookLevel | null = null;
    selectedLevelTwo: IBookLevel | null = null;
    selectedLevelThree: IBookLevel | null = null;
    selectedLevelFour: IBookLevel | null = null;
    selectedLevelFive: IBookLevel | null = null; // הוספת בחירה לרמה חמישית

    // Text entities for display
    textEntities: ITextEntity[] = [];
    selectedTextEntity: ITextEntity | null = null;

    constructor(rootStore: IRootStore) {
        makeAutoObservable(this);
        this.rootStore = rootStore;
    }

    /**
     * Sets the loading state
     * @param loading - Loading state to set
     */
    setIsLoading = (loading: boolean) => {
        this.isLoading = loading;
    };

    /**
     * Initializes hierarchical levels from content data
     * Only sets levelOne - other levels are updated via setSelectedLevelOne cascade
     * @param content - The content data to set levels from
     */
    setLevels(content: any) {
        this.levelOne = content || [];
        // Don't set levelTwo, levelThree, etc. here - they will be set by setSelectedLevelOne
        // This ensures the hierarchy matches the selected item, not just the first item
    }

    /**
     * Initializes the default book selection
     * Only called when no book is currently selected
     */
    initializeDefaultBook = () => {
        runInAction(() => {
            if (!this.selectedBook && this.books.length > 0) {
                this.setSelectedBook(this.books[0]);
            }
        });
    };

    /**
     * Initializes book and selectors from lastReading data
     * @param lastReading - The lastReading data from userData
     */
    initializeFromLastReading = async (lastReading: any) => {
        if (!lastReading) {
            this.initializeDefaultBook();
            return;
        }


        try {
            // Map book name to book name_en for finding the book
            const bookNameToNameEn: { [key: string]: string } = {
                'תורה': 'Torah',
                'משנה': 'Mishna',
                'נביאים': 'Neviim',
                'כתובים': 'Ketuvim',
                'סידור': 'Siddur_Ashkenaz',
                'סידור אשכנז': 'Siddur_Ashkenaz' // הוספת גרסה נוספת
            };

            const bookName = bookNameToNameEn[lastReading.book];
            if (!bookName) {
                this.initializeDefaultBook();
                return;
            }

            // Find and set the book
            const book = this.books.find(b => b.name_en === bookName);
            if (!book) {
                this.initializeDefaultBook();
                return;
            }

            this.setSelectedBook(book);

            // Wait for content to be loaded by useBookContent hook
            // Hook will sync data to store, so we wait for levelOne to be populated
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds max wait (50 * 100ms)
            while (this.levelOne.length === 0 && attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 100));
                attempts++;
            }
            
            if (this.levelOne.length === 0) {
                console.warn('⚠️ Content not loaded after waiting, proceeding anyway');
            }

            // Set the reading context based on the book structure
            let success = false;
            
            if (bookName === 'Torah') {
                // Torah navigates חומש → פרק → פסוק. The parasha is a bookmark,
                // not a level, so lastReading.subCategory is intentionally unused
                // here. Readings saved before chapters were renumbered carry the
                // old per-parasha label (e.g. "פרק א" for בראשית ו) and will not
                // match — setReadingContext fails and the default selection applies.
                success = await this.setReadingContext({
                    bookName,
                    levelOneLabel: lastReading.category,      // חומש
                    levelTwoLabel: lastReading.topSection,    // פרק
                    levelThreeLabel: lastReading.section      // פסוק
                });
            } else if (bookName === 'Mishna') {
                // Mishna: levelOne = מסכת, levelTwo = פרק, levelThree = משנה
                success = await this.setReadingContext({
                    bookName: bookName,
                    levelOneLabel: lastReading.subCategory, // מסכת ברכות
                    levelTwoLabel: lastReading.topSection,  // פרק ב (now available)
                    levelThreeLabel: lastReading.section    // משנה ג
                });
            } else if (bookName === 'Neviim' || bookName === 'Ketuvim') {
                // Neviim/Ketuvim navigate חלק → ספר → פרק → פסוק
                success = await this.setReadingContext({
                    bookName,
                    levelOneLabel: lastReading.category,      // חלק (ראשונים/אחרונים)
                    levelTwoLabel: lastReading.subCategory,   // ספר
                    levelThreeLabel: lastReading.topSection   // פרק
                });
            } else if (bookName === 'Siddur_Ashkenaz') {
                // Siddur: category = שער, subCategory = חלק, topSection = נושא, section = תפילה/ברכה
                success = await this.setReadingContext({
                    bookName,
                    levelOneLabel: lastReading.category,
                    levelTwoLabel: lastReading.subCategory,
                    levelThreeLabel: lastReading.topSection,
                    levelFourLabel: lastReading.section
                });
            }

            if (!success) {
                // Fallback to default selections
                if (this.levelOne.length > 0) {
                    this.setSelectedLevelOne(this.levelOne[0]);
                }
            }
        } catch (error) {
            console.error('❌ Error initializing from lastReading:', error);
            this.initializeDefaultBook();
        }
    };

    /**
     * Sets the selected book
     * Note: Data fetching is handled by useBookContent hook in components.
     * This method only updates UI state.
     * @param book - The book to select
     */
    setSelectedBook = (book: IBook) => {
        runInAction(() => {
            const isDifferentBook = this.selectedBook?.id !== book.id;
            this.selectedBook = book;

            // Drop the previous book's menus straight away.
            //
            // Without this, changing book left the old levels in the store while
            // the new content loaded. useBookContent then declines to write data
            // whose book does not match the current selection, so nothing ever
            // replaced them, and the pickers sat on Bereshit under a heading that
            // read נביאים. Clearing lets the selectors fall back to the loading
            // state they already render when selectedLevelOne is null.
            if (isDifferentBook) {
                this.levelOne = [];
                this.selectedLevelOne = null;
                this.levelTwo = [];
                this.selectedLevelTwo = null;
                this.levelThree = [];
                this.selectedLevelThree = null;
                this.levelFour = [];
                this.selectedLevelFour = null;
                this.levelFive = [];
                this.selectedLevelFive = null;
                this.textEntities = [];
                this.selectedTextEntity = null;
            }
        });
        // Data fetching is handled by useBookContent hook
        // Hook will automatically sync data to this store via useEffect
    };

    /**
     * Torah only: jump to the chapter and verse a parasha opens at.
     *
     * Parshiot are not a level of the hierarchy, because chapters cross their
     * boundaries. They are shortcuts into the chumash/chapter/verse run, so
     * selecting one lands on a real chapter and its opening verse.
     */
    /**
     * Torah only: the parasha the current position actually falls inside.
     *
     * A chapter that straddles a boundary belongs to two parshiot, so the
     * chapter alone cannot answer this — שמות ו is שמות at פסוק א and וארא
     * from פסוק ב onwards. Resolving it needs the verse as well, matched
     * against the jump index on the chumash (which the server sorts by start).
     */
    get currentParasha(): IParashaJump | null {
        const parshiot = this.selectedLevelOne?.parshiot;
        const perek = this.selectedLevelTwo?.perek;
        if (!parshiot?.length || typeof perek !== 'number') return null;

        const pasuk = this.selectedLevelThree?.pasuk ?? 1;

        let current: IParashaJump | null = null;
        for (const parasha of parshiot) {
            const hasStarted =
                parasha.perek < perek ||
                (parasha.perek === perek && parasha.pasuk <= pasuk);
            if (!hasStarted) break;
            current = parasha;
        }
        return current ?? parshiot[0];
    }

    jumpToParasha = (parasha: IParashaJump) => {
        const chapter = this.levelTwo.find((item) => item.perek === parasha.perek);
        if (!chapter) {
            console.warn('⚠️ jumpToParasha: chapter not found', parasha);
            return;
        }

        this.setSelectedLevelTwo(chapter);

        // setSelectedLevelTwo has already repopulated levelThree.
        const verse = this.levelThree.find((item) => item.label === `פסוק ${numberToGematria(parasha.pasuk)}`);
        if (verse) {
            this.setSelectedLevelThree(verse);
        }
    };


    /**
     * Updates selection at level one and cascades updates to lower levels
     */
    setSelectedLevelOne = (levelOne: IBookLevel) => {
        runInAction(() => {
            this.selectedLevelOne = levelOne;
            if (levelOne.children?.length > 0) {
                this.levelTwo = levelOne.children;
                this.setSelectedLevelTwo(levelOne.children[0]);
            } else {
                this.levelTwo = [];
                this.selectedLevelTwo = null;
                this.levelThree = [];
                this.selectedLevelThree = null;
                this.levelFour = [];
                this.selectedLevelFour = null;
                this.levelFive = [];
                this.selectedLevelFive = null;
                this.textEntities = formatResponseToTextEntities([levelOne as any], this.selectedBook?.name_en);
                this.setSelectedTextEntity(this.textEntities[0]);
            }
        });
    };

    /**
     * Updates selection at level two and cascades updates to lower levels
     */
    setSelectedLevelTwo = (levelTwo: IBookLevel) => {
        runInAction(() => {
            this.selectedLevelTwo = levelTwo;
            if (levelTwo.children?.length > 0) {
                this.levelThree = levelTwo.children;
                this.setSelectedLevelThree(levelTwo.children[0]);
            } else {
                this.levelThree = [];
                this.selectedLevelThree = null;
                this.levelFour = [];
                this.selectedLevelFour = null;
                this.levelFive = [];
                this.selectedLevelFive = null;
                this.textEntities = formatResponseToTextEntities([levelTwo as any], this.selectedBook?.name_en);
                this.setSelectedTextEntity(this.textEntities[0]);
            }
        });
    };

    /**
     * Updates selection at level three and handles display formatting
     */
    setSelectedLevelThree = (levelThree: IBookLevel) => {
        runInAction(() => {
            let displayLabel = levelThree.label;

            // Convert numeric labels to Hebrew format for better display.
            // Level three is a משנה in Mishna, a פסוק in Torah (חומש → פרק → פסוק)
            // and a פרק in Neviim/Ketuvim (חלק → ספר → פרק → פסוק).
            if (/^\d+$/.test(levelThree.label)) {
                if (this.selectedBook?.name_en === 'Mishna') {
                    displayLabel = `משנה ${numberToGematria(Number(levelThree.label))}`;
                } else if (this.selectedBook?.name_en === 'Torah') {
                    displayLabel = `פסוק ${numberToGematria(Number(levelThree.label))}`;
                } else if (this.selectedBook?.name_en === 'Neviim' || this.selectedBook?.name_en === 'Ketuvim') {
                    displayLabel = `פרק ${numberToGematria(Number(levelThree.label))}`;
                }
            }

            const displayLevelThree = { ...levelThree, label: displayLabel };
            this.selectedLevelThree = displayLevelThree;

            if (levelThree.children?.length > 0) {
                this.levelFour = levelThree.children;
                this.setSelectedLevelFour(levelThree.children[0]);
            } else {
                // This is the deepest level - create text entities
                this.levelFour = [];
                this.selectedLevelFour = null;
                this.levelFive = [];
                this.selectedLevelFive = null;
                this.textEntities = formatResponseToTextEntities([levelThree as any], this.selectedBook?.name_en);
                this.setSelectedTextEntity(this.textEntities[0]);
            }
        });
    };

    /**
     * Updates selection at level four
     */
    setSelectedLevelFour = (levelFour: IBookLevel) => {
        runInAction(() => {
            this.selectedLevelFour = levelFour;
            
            if (levelFour.children?.length > 0) {
                // יש רמה חמישית (sub_sections בסידור)
                this.levelFive = levelFour.children;
                this.setSelectedLevelFive(levelFour.children[0]);
            } else {
                // אין רמה חמישית - זו הרמה העמוקה ביותר
                this.levelFive = [];
                this.selectedLevelFive = null;
                this.textEntities = formatResponseToTextEntities([levelFour as any], this.selectedBook?.name_en);
                this.setSelectedTextEntity(this.textEntities[0]);
            }
        });
    };

    /**
     * Updates selection at level five (deepest level for Siddur)
     */
    setSelectedLevelFive = (levelFive: IBookLevel) => {
        runInAction(() => {
            this.selectedLevelFive = levelFive;
            this.textEntities = formatResponseToTextEntities([levelFive as any], this.selectedBook?.name_en);
            this.setSelectedTextEntity(this.textEntities[0]);
        });
    };

    /**
     * Sets the currently selected text entity
     */
    setSelectedTextEntity = (textEntity: ITextEntity) => {
        runInAction(() => {
            this.selectedTextEntity = textEntity;
        });
    };

    /**
     * Gets the next or previous item in a list of siblings
     */
    getNextItem = (currentItem: IBookLevel | null, siblings: IBookLevel[], isNext: boolean): IBookLevel | null => {
        if (!currentItem) return null;

        const currentIndex = siblings.findIndex(item => item.id === currentItem.id);
        if (currentIndex === -1) return null;

        const nextIndex = isNext ? currentIndex + 1 : currentIndex - 1;
        return nextIndex >= 0 && nextIndex < siblings.length ? siblings[nextIndex] : null;
    };

    /**
     * Navigates to the next or previous entity in the hierarchy
     */
    goNextOrPrevEntity = (isNext: boolean) => {
        runInAction(() => {
            if (!this.selectedTextEntity || !this.textEntities?.length) return;

            // Find current index in textEntities array
            const currentIndex = this.textEntities.findIndex(
                entity => entity.index === this.selectedTextEntity?.index
            );

            if (currentIndex === -1) return;

            // Calculate next index
            const nextIndex = isNext ? currentIndex + 1 : currentIndex - 1;

            // Check if we can move within current textEntities
            if (nextIndex >= 0 && nextIndex < this.textEntities.length) {
                this.setSelectedTextEntity(this.textEntities[nextIndex]);
                return;
            }

            // If we can't move within textEntities, try to move to next/previous level
            // רמה חמישית (sub_sections בסידור)
            if (this.selectedLevelFive) {
                const nextLevelFive = this.getNextItem(this.selectedLevelFive, this.levelFive, isNext);
                if (nextLevelFive) {
                    this.setSelectedLevelFive(nextLevelFive);
                    return;
                }
            }

            if (this.selectedLevelFour) {
                const nextLevelFour = this.getNextItem(this.selectedLevelFour, this.levelFour, isNext);
                if (nextLevelFour) {
                    this.setSelectedLevelFour(nextLevelFour);
                    return;
                }
            }

            if (this.selectedLevelThree) {
                const nextLevelThree = this.getNextItem(this.selectedLevelThree, this.levelThree, isNext);
                if (nextLevelThree) {
                    this.setSelectedLevelThree(nextLevelThree);
                    return;
                }
            }

            if (this.selectedLevelTwo) {
                const nextLevelTwo = this.getNextItem(this.selectedLevelTwo, this.levelTwo, isNext);
                if (nextLevelTwo) {
                    this.setSelectedLevelTwo(nextLevelTwo);
                    return;
                }
            }

            if (this.selectedLevelOne) {
                const nextLevelOne = this.getNextItem(this.selectedLevelOne, this.levelOne, isNext);
                if (nextLevelOne) {
                    this.setSelectedLevelOne(nextLevelOne);
                }
            }
        });
    };

    /**
     * Converts mishna data to masechet format
     */
    convertMishnaToMasechet = (subCategoryData: any) => {
        runInAction(() => {
            if (subCategoryData.sub_category_name_he && subCategoryData.sub_category_name_he.includes("משנה")) {
                subCategoryData.sub_category_name_he = subCategoryData.sub_category_name_he.replace("משנה", "מסכת");
            }
        });
        return [];
    };

    /**
     * Sets reading context programmatically
     */
    setReadingContext = async (readingContext: IReadingContext): Promise<boolean> => {
        const { bookName, levelOneLabel, levelTwoLabel, levelThreeLabel, levelFourLabel, levelFiveLabel } = readingContext;


        try {
            // Find and set book
            const book = this.books.find(b => b.name_en === bookName);
            if (!book) {
                return false;
            }

            this.setSelectedBook(book);

            // Wait for content to be loaded by useBookContent hook
            // Hook will sync data to store, so we wait for levelOne to be populated
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds max wait (50 * 100ms)
            while (this.levelOne.length === 0 && attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 100));
                attempts++;
            }
            
            if (this.levelOne.length === 0) {
                console.warn('⚠️ Content not loaded after waiting in setReadingContext');
                return false;
            }

            // אם יש levelOneLabel, נשתמש בו כדי למצוא את הקטע הנכון
            if (levelOneLabel) {                
                // Handle different book structures based on book type
                if (bookName === 'Mishna') {
                    // Mishna structure: levelOne = מסכת, levelTwo = פרק, levelThree = משנה
                    const levelOne = this.levelOne.find(item => item.label === levelOneLabel);
                    if (!levelOne) {
                        return false;
                    }

                    this.setSelectedLevelOne(levelOne);

                    if (levelTwoLabel) {
                        const levelTwo = this.levelTwo.find(item => item.label === levelTwoLabel);
                        if (!levelTwo) {
                            return false;
                        }

                        this.setSelectedLevelTwo(levelTwo);

                        if (levelThreeLabel) {
                            const levelThree = this.levelThree.find(item => item.label === levelThreeLabel);
                            if (!levelThree) {
                                return false;
                            }
                            this.setSelectedLevelThree(levelThree);
                        }
                    }
                } else if (bookName === 'Torah') {
                    // Torah structure: levelOne = חומש, levelTwo = פרק, levelThree = פסוק
                    // (levelFourLabel is unused — kept so the walk stays generic)
                    const levelOne = this.levelOne.find(item => item.label === levelOneLabel);
                    if (!levelOne) {
                        return false;
                    }

                    this.setSelectedLevelOne(levelOne);

                    if (levelTwoLabel) {
                        const levelTwo = this.levelTwo.find(item => item.label === levelTwoLabel);
                        if (!levelTwo) {
                            return false;
                        }

                        this.setSelectedLevelTwo(levelTwo);

                        if (levelThreeLabel) {
                            const levelThree = this.levelThree.find(item => item.label === levelThreeLabel);
                            if (!levelThree) {
                                return false;
                            }
                            this.setSelectedLevelThree(levelThree);

                            if (levelFourLabel) {
                                const levelFour = this.levelFour.find(item => item.label === levelFourLabel);
                                if (!levelFour) {
                                    return false;
                                }
                                this.setSelectedLevelFour(levelFour);
                            }
                        }
                    }
                } else if (bookName === 'Siddur_Ashkenaz') {
                    // Siddur structure: levelOne = שער, levelTwo = חלק, levelThree = נושא, levelFour = תפילה/ברכה, levelFive = תת-חלק
                    const levelOne = this.levelOne.find(item => item.label === levelOneLabel);
                    if (!levelOne) {
                        return false;
                    }

                    this.setSelectedLevelOne(levelOne);

                    if (levelTwoLabel) {
                        const levelTwo = this.levelTwo.find(item => item.label === levelTwoLabel);
                        if (!levelTwo) {
                            return false;
                        }
                        this.setSelectedLevelTwo(levelTwo);

                        if (levelThreeLabel) {
                            const levelThree = this.levelThree.find(item => item.label === levelThreeLabel);
                            if (!levelThree) {
                                return false;
                            }
                            this.setSelectedLevelThree(levelThree);

                            if (levelFourLabel) {
                                const levelFour = this.levelFour.find(item => item.label === levelFourLabel);
                                if (!levelFour) {
                                    return false;
                                }
                                this.setSelectedLevelFour(levelFour);

                                if (levelFiveLabel) {
                                    const levelFive = this.levelFive.find(item => item.label === levelFiveLabel);
                                    if (!levelFive) {
                                        return false;
                                    }
                                    this.setSelectedLevelFive(levelFive);
                                }
                            }
                        }
                    }
                } else if (bookName === 'Neviim' || bookName === 'Ketuvim') {                                        
                    // Find the part in levelOne
                    const levelOne = this.levelOne.find(item => item.label === levelOneLabel);
                    if (!levelOne) {
                        return false;
                    }
                    this.setSelectedLevelOne(levelOne);

                    if (levelTwoLabel) {
                        const levelTwo = this.levelTwo.find(item => item.label === levelTwoLabel);
                        if (!levelTwo) {
                            return false;
                        }

                        this.setSelectedLevelTwo(levelTwo);

                        if (levelThreeLabel) {
                            const levelThree = this.levelThree.find(item => item.label === levelThreeLabel);
                            if (!levelThree) {
                                return false;
                            }
                            this.setSelectedLevelThree(levelThree);

                            if (levelFourLabel) {
                                const levelFour = this.levelFour.find(item => item.label === levelFourLabel);
                                if (!levelFour) {
                                    return false;
                                }
                                this.setSelectedLevelFour(levelFour);
                            }
                        }
                    }
                }

                return true;
            }

            return true;
        } catch (error) {
            console.error('❌ Error setting reading context:', error);
            return false;
        }
    };

    // Cache management is now handled by TanStack Query hooks:
    // - useRefreshBook() - for refreshing a specific book
    // - useClearBookCache() - for clearing all book caches
    // - useBookContent() - automatically checks version and clears cache when version changes
}

export default BooksController; 
